import React, {useEffect, useState} from "react";
import {Checkbox, FormGroup, useMediaQuery} from "@mui/material";
import Popup from "../../components/Popup";
import {httpClient} from "../../funcs";
import {notifyAboutError, notifyUser} from "../../methods/notifyUser";
import {mobileMaxWidthMediaQuery} from "../../methods/utils";
import {
    accessLevelPermissions,
    TEMPLATE_PERMISSION_OPTIONS,
    togglePermission,
} from "../../methods/templatePermissions";
import {ReactComponent as CheckboxIcon} from "pages/Icons/size24/Checkbox.svg";
import {ReactComponent as CheckboxChecked} from "pages/Icons/size24/CheckboxChecked.svg";
import {PageLoader} from "../Contracts/ContractList/PageLoader";

const PAGE_SIZE = 10;

type TemplateRow = {
    _id: string;
    name?: string;
};

type GrantMode = "off" | "on";

function permissionCheckboxes(
    current: string[],
    onToggle: (permissionId: string) => void,
) {
    return (
        <FormGroup>
            {TEMPLATE_PERMISSION_OPTIONS.map((permission) => {
                const hasAvailable = current.includes("available");
                if (permission.id !== "available" && !hasAvailable) {
                    return null;
                }
                return (
                    <div
                        className="titleContainer"
                        key={permission.id}
                        style={{marginTop: "10px"}}
                    >
                        <div>
                            <div className="Subheader_17">{permission.label}</div>
                            <div className="commonSubtitle">{permission.description}</div>
                        </div>
                        <Checkbox
                            icon={<CheckboxIcon className="icon"/>}
                            checkedIcon={<CheckboxChecked className="icon"/>}
                            checked={current.includes(permission.id)}
                            onChange={() => onToggle(permission.id)}
                        />
                    </div>
                );
            })}
        </FormGroup>
    );
}

function EmployeeTemplatesAccessPopup(props: {
    role: any;
    onClose: () => void;
    onSaved: () => void;
    $store?: any;
    $commitToStore?: (data: any) => void;
}) {
    const mobile = useMediaQuery(mobileMaxWidthMediaQuery);
    const role = props.role || {};
    // Без поля template_scope — старое поведение (все шаблоны).
    const initialScope = (
        role.template_scope === "selected"
            ? "selected"
            : "all"
    );
    const [scope, setScope] = useState<"all" | "selected">(initialScope);
    const [grants, setGrants] = useState<{[id: string]: string[]}>({});
    const [modes, setModes] = useState<{[id: string]: GrantMode}>({});
    const [templates, setTemplates] = useState<TemplateRow[]>([]);
    const [hasMoreTemplates, setHasMoreTemplates] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultGrant = accessLevelPermissions(role.access_level);

    useEffect(() => {
        const stored = role.allowed_templates || {};
        const nextModes: {[id: string]: GrantMode} = {};
        const nextGrants: {[id: string]: string[]} = {};
        Object.keys(stored).forEach((templateId) => {
            nextModes[templateId] = "on";
            nextGrants[templateId] = [...(stored[templateId] || [])];
        });
        setModes(nextModes);
        setGrants(nextGrants);
        loadTemplates(0, true);
    }, []);

    async function loadTemplates(offset: number, reset: boolean) {
        if (!reset && loadingMore) {
            return;
        }
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const {data} = await httpClient.get("/templates", {
                params: {limit: PAGE_SIZE, offset},
            });
            const chunk: TemplateRow[] = Array.isArray(data) ? data : (data?.templates || []);
            setTemplates((prev) => (reset ? chunk : [...prev, ...chunk]));
            setHasMoreTemplates(chunk.length === PAGE_SIZE);
        } catch (error) {
            notifyAboutError(props, error);
            setHasMoreTemplates(false);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }

    function setTemplateGranted(templateId: string, on: boolean) {
        setModes({...modes, [templateId]: on ? "on" : "off"});
        if (on && !Object.prototype.hasOwnProperty.call(grants, templateId)) {
            setGrants({...grants, [templateId]: [...defaultGrant]});
        }
    }

    function buildAllowedTemplates() {
        const result: {[id: string]: string[]} = {};
        Object.keys(modes).forEach((templateId) => {
            if (modes[templateId] !== "on") {
                return;
            }
            result[templateId] = grants[templateId] || [];
        });
        return result;
    }

    async function save() {
        setSaving(true);
        try {
            const payload: any = {
                _id: role._id,
                template_scope: scope,
            };
            if (scope === "selected") {
                payload.allowed_templates = buildAllowedTemplates();
            }
            await httpClient.patch("/update-role", payload);
            notifyUser(props, "Доступ к шаблонам обновлён");
            props.onSaved();
        } catch (error) {
            notifyAboutError(props, error);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Popup maxWidth="720px" onClose={props.onClose}>
            <div style={{width: mobile ? "100%" : "560px"}}>
                <div className="commonTitle" style={{marginBottom: "10px"}}>
                    Доступ к шаблонам
                </div>
                <div className="commonSubtitle" style={{marginBottom: "16px"}}>
                    По умолчанию сотрудник ничего не видит. Выдайте нужные шаблоны —
                    договоры по остальным тоже не появятся в списке.
                </div>
                {role.access_profile_id && (
                    <div className="commonSubtitle" style={{marginBottom: "16px", color: "#E65100"}}>
                        Сейчас действует своя роль доступа. Сохранение «только выбранных»
                        снимет её с сотрудника.
                    </div>
                )}
                {loading ? (
                    <PageLoader/>
                ) : (
                    <>
                        <div style={{display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px"}}>
                            <button
                                type="button"
                                className={`new action-button ${scope === "selected" ? "primary" : "secondary"}`}
                                onClick={() => setScope("selected")}
                            >
                                <span className="button-text">Только выбранные</span>
                            </button>
                            <button
                                type="button"
                                className={`new action-button ${scope === "all" ? "primary" : "secondary"}`}
                                onClick={() => setScope("all")}
                            >
                                <span className="button-text">Все шаблоны</span>
                            </button>
                        </div>

                        {scope === "all" ? (
                            <div className="commonSubtitle">
                                Видны все шаблоны аккаунта, включая новые.
                                Уровень доступа и своя роль задают права.
                            </div>
                        ) : (
                            <>
                                {templates.length === 0 && !hasMoreTemplates && (
                                    <div className="commonSubtitle">Шаблонов пока нет</div>
                                )}
                                {templates.map((template) => {
                                    const granted = (modes[template._id] || "off") === "on";
                                    return (
                                        <div
                                            key={template._id}
                                            className="templateViewContainer disabledHover"
                                            style={{marginBottom: "12px"}}
                                        >
                                            <div className="Subheader_17" style={{marginBottom: "8px"}}>
                                                {template.name || "Без названия"}
                                            </div>
                                            <div style={{display: "flex", flexWrap: "wrap", gap: "8px"}}>
                                                <button
                                                    type="button"
                                                    className={`new action-button ${!granted ? "primary" : "secondary"}`}
                                                    onClick={() => setTemplateGranted(template._id, false)}
                                                >
                                                    <span className="button-text">Не выдан</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`new action-button ${granted ? "primary" : "secondary"}`}
                                                    onClick={() => setTemplateGranted(template._id, true)}
                                                >
                                                    <span className="button-text">Выдан</span>
                                                </button>
                                            </div>
                                            {granted && permissionCheckboxes(
                                                grants[template._id] || [],
                                                (permissionId) => {
                                                    setGrants({
                                                        ...grants,
                                                        [template._id]: togglePermission(
                                                            grants[template._id],
                                                            permissionId,
                                                        ),
                                                    });
                                                },
                                            )}
                                            {granted && !(grants[template._id] || []).includes("available") && (
                                                <div className="commonSubtitle" style={{marginTop: "8px"}}>
                                                    Без «Базовый доступ» — только просмотр шаблона и договоров.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {hasMoreTemplates && (
                                    <button
                                        className="new withTitle secondary _wa"
                                        disabled={loadingMore}
                                        onClick={() => loadTemplates(templates.length, false)}
                                    >
                                        {loadingMore ? "Загрузка…" : "Показать ещё"}
                                    </button>
                                )}
                            </>
                        )}

                        <hr className="newHR"/>
                        <div className="buttonsContainer" style={{width: "100%"}}>
                            <button
                                className="new withTitle _wa"
                                disabled={saving}
                                onClick={save}
                            >
                                Сохранить
                            </button>
                            <button
                                className="new withTitle secondary _wa"
                                onClick={props.onClose}
                            >
                                Отмена
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Popup>
    );
}

export default EmployeeTemplatesAccessPopup;
